import axios, { type AxiosResponse } from 'axios';
import dayjs from 'dayjs';

import type { User } from '@/types/database/users';

import type {
  CleanReservationDetailsResponse,
  CleanReserveList,
  CleanServiceItem,
  MemberInfoAndTicketList,
} from '../sharedTypes';
import type { MembershipInformation, ReservationDateStatus } from './types';

const getMemberInfos = (
  users: (User & MembershipInformation & { _id: string })[]
) => {
  const memberInfos = users.map((user) => {
    const { userExtraInfo } = user;
    return {
      ...user,
      ...userExtraInfo,
    };
  });

  return memberInfos;
};

export const getDataSource = (
  users: (User &
    MembershipInformation & {
      _id: string;
    })[],
  searchValue: string
) => {
  let dataSource = getMemberInfos(users);

  if (searchValue) {
    dataSource = dataSource.filter((memberInfo) => {
      return memberInfo.phoneNumber?.includes(searchValue);
    });
  }

  return dataSource;
};

export const getCleanKindOptions = (
  memberListAndTicketList: MemberInfoAndTicketList[] | undefined,
  userId: string
) => {
  const foundMemberInfoAndCleanKind = memberListAndTicketList?.find(
    (memberInfoAndCleanKind) => memberInfoAndCleanKind.userId === userId
  );

  const options = foundMemberInfoAndCleanKind?.cleanKinds.map((cleanKind) => {
    const { cleanKindName, cleanKindId } = cleanKind;
    return {
      label: cleanKindName,
      value: cleanKindId,
    };
  });

  return options ?? [];
};

export const getCommunityAndHouseholdNumberOptions = (
  memberListAndTicketList: MemberInfoAndTicketList[] | undefined,
  userId: string
) => {
  const foundMemberInfoAndCleanKind = memberListAndTicketList?.find(
    (memberInfoAndCleanKind) => memberInfoAndCleanKind.userId === userId
  );

  const options = foundMemberInfoAndCleanKind?.cleanKinds.reduce(
    (
      options: {
        label: string;
        value: string;
      }[],
      cleanKind
    ) => {
      const { communityName, householdNumber } = cleanKind;
      const label = `${communityName} ${householdNumber}`;
      const isOptionExist = options.find((option) => option.label === label);
      if (!isOptionExist) {
        const option = {
          label,
          value: [communityName, householdNumber].join('/'),
        };
        options.push(option);
      }
      return options;
    },
    []
  );

  return options ?? [];
};

export const getCleanKindInformation = async (cleanKindId: string) => {
  try {
    const res: AxiosResponse<CleanReservationDetailsResponse> = await axios({
      method: 'GET',
      url: `/ezcon/api/cleanReservationDetails/${cleanKindId}`,
      headers: {
        authorization: localStorage.getItem('feathers-jwt'),
      },
    });
    return res.data;
  } catch (error) {
    throw new Error();
  }
};

export const getMemberPhoneNumberOptions = (
  memberListAndTicketList: MemberInfoAndTicketList[] | undefined,
  userId: string
) => {
  let list = memberListAndTicketList;
  if (userId) {
    list = list?.filter((memberInfo) => memberInfo.userId.includes(userId));
  }
  const options = list?.map((memberInfoAndCleanKind) => ({
    label: memberInfoAndCleanKind.phoneNumber,
    value: memberInfoAndCleanKind.userId,
  }));

  return options;
};

export const getReservationDateStatus = async (args: {
  cleanKindId: string;
  month: number;
  communityName: string;
  householdNumber: string;
  userId: string;
  ticketId: string;
  selectedDates: number[];
}) => {
  const { cleanKindId, ...params } = args;
  try {
    const res: AxiosResponse<ReservationDateStatus> = await axios({
      method: 'GET',
      url: `/ezcon/api/cleanKind/${cleanKindId}/reservation`,
      headers: {
        authorization: localStorage.getItem('feathers-jwt'),
      },
      params,
    });

    return res.data;
  } catch (error) {
    throw new Error('查詢預約日期狀態失敗!');
  }
};

export const getTicketId = (
  cleanServiceItems: CleanServiceItem[] | undefined
) => {
  const freeItem = cleanServiceItems?.find(
    (item) => item.priceInfo.kind === 'Free'
  );
  const ticketId =
    freeItem?.priceInfo.kind === 'Free' &&
    freeItem.priceInfo.availableTicketId[0];

  return ticketId;
};

export const fetchReservationDatesStatus = async (args: {
  cleanKindInformation: CleanReservationDetailsResponse | undefined;
  cleanReserveList: CleanReserveList | undefined;
  userId: string;
  selectedDates: number[];
}) => {
  const { cleanKindInformation, cleanReserveList, userId, selectedDates } =
    args;
  const ticketId = getTicketId(cleanKindInformation?.cleanServiceItems);
  if (cleanReserveList && ticketId) {
    const thisMonth = dayjs().valueOf();
    const newDatesStatusInfo = await getReservationDateStatus({
      cleanKindId: cleanReserveList.cleanKindId,
      month: thisMonth,
      communityName: cleanReserveList.communityName,
      householdNumber: cleanReserveList.householdNumber,
      userId,
      ticketId,
      selectedDates,
    });

    return newDatesStatusInfo.days;
  }

  return [];
};
